import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { MessageCircle, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/config/api';
import { trackFeature } from '@/lib/analytics';

export default function ContactAdmin({ trigger }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (subject.trim().length < 2 || body.trim().length < 2) {
      toast.error('Please enter a subject and message');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/admin-messages`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: subject.trim(),
          body: body.trim(),
          contact_email: contactEmail.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success("Message sent — we'll get back to you soon");
        trackFeature('contact', 'message_sent');
        setSubject('');
        setBody('');
        setContactEmail('');
        setOpen(false);
      } else {
        toast.error(data.detail || 'Failed to send');
      }
    } catch (e) {
      toast.error(`Network error: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {trigger ? (
        <span onClick={() => setOpen(true)}>{trigger}</span>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          data-testid="contact-admin-button"
          className="gap-1.5"
        >
          <MessageCircle className="h-3.5 w-3.5" /> Contact us
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="contact-admin-dialog">
          <DialogHeader>
            <DialogTitle>Contact the team</DialogTitle>
            <DialogDescription>
              Send us feedback, report a bug, or ask anything. We read every message.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Subject</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. Bug report: dose log saved wrong amount"
                maxLength={200}
                data-testid="contact-subject-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Message</label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Describe what happened, what you tried, and any error messages…"
                rows={6}
                maxLength={4000}
                data-testid="contact-body-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                Reply-to email <span className="text-muted-foreground">(optional — defaults to your account email)</span>
              </label>
              <Input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="another@email.com"
                data-testid="contact-email-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={sending}>Cancel</Button>
            <Button onClick={submit} disabled={sending} data-testid="contact-send-button">
              {sending ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Sending…</> : <><Send className="h-3.5 w-3.5 mr-1.5" /> Send</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
